// IndexedDB stores for the client: HTTP cache (ETag), blobs (by SHA), queued
// review-mark writes, and unsent comment text.
//
// The database is per signed-in user (`changelist:<user ID>`), so one
// browser profile never shows a user another user's cached repository data.
// Until the session says who is signed in, every call waits; when nobody is
// signed in, IndexedDB is not used, and databases left by earlier users are
// deleted (see `setDbUser`).
import { openDB, deleteDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { CachedResponse } from './cache';
import { selectIdentity } from './identity';
import type { WireAnchor } from './server';

/** One queued review-mark write, keyed `<pr key>|<block key>`. */
export interface PendingMarkRecord {
  /** `owner/repo#n`, lowercased. */
  pr: string;
  /** The anchor's ID (`review/anchors.ts` `anchorId`); for a write queued
   *  before marks were anchors, the key-based mark's key. */
  block_key: string;
  /** The anchor written (marks API version 2); absent for a key-based
   *  write an older client queued, which is replayed by the key API. */
  anchor?: WireAnchor;
  reviewed: boolean;
  /** Unique per write, so a tab deletes only the record it sent. */
  id: string;
}

interface Schema extends DBSchema {
  http: { key: string; value: CachedResponse; indexes: { at: number } };
  blobs: { key: string; value: { sha: string; text: string | null; binary: boolean; at?: number }; indexes: { at: number } };
  marks: { key: string; value: PendingMarkRecord };
  drafts: { key: string; value: { key: string; body: string; at: number } };
  kv: { key: string; value: unknown };
}

const PREFIX = 'changelist';
/** Entries kept in the HTTP and blob caches (oldest by `at` pruned). */
export const HTTP_CACHE_LIMIT = 2000;
export const BLOB_CACHE_LIMIT = 5000;
/** A prune trims a cache to this share of its limit, so the next write does not prune again. */
const PRUNE_TO = 0.9;
/** Schema version: 2 added the blobs `at` index. */
const DB_VERSION = 2;

type User = string | null;
let user: User | undefined;
let userWaiters: ((u: User) => void)[] = [];
let dbp: Promise<IDBPDatabase<Schema> | null> | null = null;

function dbName(u: string): string {
  return `${PREFIX}:${u}`;
}

function whoIsSignedIn(): Promise<User> {
  if (user !== undefined) return Promise.resolve(user);
  return new Promise((r) => userWaiters.push(r));
}

/**
 * Select the signed-in user's database (null when signed out). Databases that
 * belong to anyone else, including the pre-namespacing `changelist` one, are
 * deleted, so cached source and drafts never outlive the session that made
 * them on a shared browser profile.
 */
export async function setDbUser(id: number | string | null): Promise<void> {
  const next: User = id === null ? null : String(id);
  // The new account is selected before anyone hears of it, so an identity
  // listener that opens the database opens the new account's.
  const old = user !== next ? dbp : null;
  if (user !== next) dbp = null;
  user = next;
  const waiters = userWaiters;
  userWaiters = [];
  for (const w of waiters) w(next);
  selectIdentity(next);
  if (old) (await old.catch(() => null))?.close();
  await settledWithin(deleteOtherDatabases(next), DELETE_WAIT_MS);
}

/**
 * How long sign-in and sign-out wait for a database deletion. Deleting waits
 * until every other tab has closed its connection (each closes on request,
 * see `db`), and the deletion continues in the background after this.
 */
export const DELETE_WAIT_MS = 1500;

/** Resolves when `p` settles or after `ms`, whichever comes first; never rejects. */
export function settledWithin(p: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    void p.catch(() => {}).finally(() => {
      clearTimeout(t);
      resolve();
    });
  });
}

async function deleteOtherDatabases(keep: User) {
  if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') return;
  try {
    const doomed = (await indexedDB.databases())
      .map((d) => d.name)
      .filter((n): n is string => !!n && (n === PREFIX || n.startsWith(PREFIX + ':')))
      .filter((n) => keep === null || n !== dbName(keep));
    await Promise.all(doomed.map((n) => deleteDB(n, { blocked: () => {} }).catch(() => {})));
  } catch {
    /* listing unsupported or blocked: nothing to clean */
  }
}

/**
 * Delete the current user's database (sign-out). From then on this page uses
 * no IndexedDB, so a late write cannot recreate the database. Resolves once
 * it is gone or after `wait` ms, whichever comes first, so another tab that
 * does not let go of its connection never holds up the caller.
 */
export async function clearUserData(wait = DELETE_WAIT_MS): Promise<void> {
  const u = user;
  user = null;
  const old = dbp;
  dbp = null;
  selectIdentity(null);
  if (old) (await old.catch(() => null))?.close();
  if (u && typeof indexedDB !== 'undefined')
    await settledWithin(deleteDB(dbName(u), { blocked: () => {} }), wait);
}

/**
 * The signed-in user's database. The connection closes itself when another
 * tab deletes the database (sign-out, or a different user signing in), and
 * this tab then stops using IndexedDB rather than recreate what was deleted.
 */
export function db(): Promise<IDBPDatabase<Schema> | null> {
  if (!dbp) {
    const p: Promise<IDBPDatabase<Schema> | null> = whoIsSignedIn()
      .then((u) =>
        u === null || typeof indexedDB === 'undefined'
          ? null
          : openDB<Schema>(dbName(u), DB_VERSION, {
              async upgrade(d, oldVersion, _newVersion, tx) {
                if (oldVersion < 1) {
                  d.createObjectStore('http').createIndex('at', 'at');
                  d.createObjectStore('blobs').createIndex('at', 'at');
                  d.createObjectStore('marks');
                  d.createObjectStore('drafts');
                  d.createObjectStore('kv');
                  return;
                }
                if (oldVersion < 2) {
                  // Blobs written before `at` was recorded count as the oldest.
                  const blobs = tx.objectStore('blobs');
                  blobs.createIndex('at', 'at');
                  for (let c = await blobs.openCursor(); c; c = await c.continue())
                    if (typeof c.value.at !== 'number') await c.update({ ...c.value, at: 0 });
                }
              },
              blocking(_current, next, event) {
                (event.target as IDBDatabase).close();
                if (dbp !== p) return;
                // `next` is null when the database is being deleted.
                dbp = next === null ? Promise.resolve(null) : null;
                if (next === null) {
                  selectIdentity(null);
                  if (typeof window !== 'undefined') window.dispatchEvent(new Event('changelist:session-invalidated'));
                }
              },
              terminated() {
                if (dbp === p) dbp = null;
              },
            }),
      )
      .catch(() => null);
    dbp = p;
  }
  return dbp;
}

type StoreName = 'http' | 'blobs' | 'marks' | 'drafts' | 'kv';

export async function idbGet<S extends StoreName>(store: S, key: string): Promise<Schema[S]['value'] | undefined> {
  const d = await db();
  if (!d) return undefined;
  try {
    return (await d.get(store, key)) as Schema[S]['value'] | undefined;
  } catch {
    return undefined;
  }
}

export async function idbSet<S extends StoreName>(store: S, key: string, value: Schema[S]['value']): Promise<void> {
  await idbPut(store, key, value);
}

/** `idbSet`, resolving to whether the value was stored (never rejects). */
export async function idbPut<S extends StoreName>(store: S, key: string, value: Schema[S]['value']): Promise<boolean> {
  const d = await db();
  if (!d) return false;
  let stored = true;
  try {
    await d.put(store, value as never, key);
  } catch {
    /* storage full or unavailable: the app keeps working without the cache */
    stored = false;
  }
  if (store === 'http' || store === 'blobs') schedulePrune();
  return stored;
}

export async function idbDelete(store: StoreName, key: string): Promise<void> {
  const d = await db();
  if (!d) return;
  try {
    await d.delete(store, key);
  } catch {
    /* ignore */
  }
}

/** Every record whose key starts with `prefix`. */
export async function idbEntries<S extends StoreName>(
  store: S,
  prefix = '',
): Promise<[string, Schema[S]['value']][]> {
  const d = await db();
  if (!d) return [];
  try {
    const range = prefix ? IDBKeyRange.bound(prefix, prefix + '￿') : undefined;
    const tx = d.transaction(store);
    const out: [string, Schema[S]['value']][] = [];
    for (let c = await tx.store.openCursor(range); c; c = await c.continue())
      out.push([String(c.key), c.value as Schema[S]['value']]);
    return out;
  } catch {
    return [];
  }
}

/**
 * Read `keys`, and write back what `next` makes of them, in one readwrite
 * transaction: an undefined value deletes its key, an unchanged one is
 * left. Resolves to whether it was written (never rejects); with no
 * database (signed out), nothing is written and it resolves true.
 */
export async function idbUpdate<S extends StoreName>(
  store: S,
  keys: string[],
  next: (values: (Schema[S]['value'] | undefined)[]) => (Schema[S]['value'] | undefined)[],
): Promise<boolean> {
  const d = await db();
  if (!d) return true;
  try {
    const tx = d.transaction(store, 'readwrite');
    const before = (await Promise.all(keys.map((k) => tx.store.get(k)))) as (Schema[S]['value'] | undefined)[];
    const after = next(before);
    await Promise.all(
      keys.map((k, i) =>
        after[i] === before[i] ? undefined : after[i] === undefined ? tx.store.delete(k) : tx.store.put(after[i] as never, k),
      ),
    );
    await tx.done;
    return true;
  } catch {
    /* storage unavailable: the app keeps working without saved drafts */
    return false;
  }
}

/**
 * Put each `[key, value]` (deleting a key whose value is undefined) in one
 * readwrite transaction, reading nothing. Resolves to whether it was
 * written (never rejects); with no database, true.
 */
export async function idbWrite<S extends StoreName>(store: S, entries: [string, Schema[S]['value'] | undefined][]): Promise<boolean> {
  const d = await db();
  if (!d || !entries.length) return true;
  try {
    const tx = d.transaction(store, 'readwrite');
    await Promise.all(entries.map(([k, v]) => (v === undefined ? tx.store.delete(k) : tx.store.put(v as never, k))));
    await tx.done;
    return true;
  } catch {
    return false;
  }
}

/** Delete `key` only if its record still satisfies `pred` (one transaction). */
export async function idbDeleteIf<S extends StoreName>(
  store: S,
  key: string,
  pred: (v: Schema[S]['value']) => boolean,
): Promise<void> {
  const d = await db();
  if (!d) return;
  try {
    const tx = d.transaction(store, 'readwrite');
    const v = (await tx.store.get(key)) as Schema[S]['value'] | undefined;
    if (v !== undefined && pred(v)) await tx.store.delete(key);
    await tx.done;
  } catch {
    /* ignore */
  }
}

let pruneTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePrune() {
  if (pruneTimer) return;
  pruneTimer = setTimeout(() => {
    pruneTimer = null;
    void pruneCaches();
  }, 5000);
}

/**
 * Trim the HTTP cache and the blob cache (blobs are content-addressed and
 * cheap to refetch) when over their limits: oldest `at` first, down to
 * PRUNE_TO of the limit. Walks the `at` index by key only, so no cached
 * body or file text is read.
 */
export async function pruneCaches(httpLimit = HTTP_CACHE_LIMIT, blobLimit = BLOB_CACHE_LIMIT): Promise<void> {
  const d = await db();
  if (!d) return;
  try {
    await pruneOldest(d, 'http', httpLimit);
    await pruneOldest(d, 'blobs', blobLimit);
  } catch {
    /* best effort */
  }
}

async function pruneOldest(d: IDBPDatabase<Schema>, store: 'http' | 'blobs', limit: number) {
  const n = await d.count(store);
  if (n <= limit) return;
  let excess = n - Math.floor(limit * PRUNE_TO);
  const tx = d.transaction(store, 'readwrite');
  for (let c = await tx.store.index('at').openKeyCursor(); c && excess > 0; c = await c.continue(), excess--)
    await tx.store.delete(c.primaryKey);
  await tx.done;
}
